import { useRef } from 'react';
import '@/styles/vip-layout.css';
import { SimpleMenu } from '@/components/MenuGrid';
import { useLocale } from '@/context/LocaleContext';
import type { VipState } from '@/lib/vipLevels';
import { useQuery } from '@tanstack/react-query';
import { api, resolveCharIdFromJwt } from '@/lib/axios';

export function VipSystemPage() {
  const { t } = useLocale();
  const { data, isLoading, isError } = useQuery({queryKey:['vip', resolveCharIdFromJwt()], queryFn:async () => (await api.get<VipState>('/api/v1/vip')).data, staleTime:30_000, refetchInterval:30_000});

  const infoRef = useRef<HTMLDialogElement>(null);
  const next = data?.levels.find(row => !row.unlocked);
  const current = data?.levels.filter(row => row.unlocked).at(-1)?.level ?? 0;
  const percent = data ? next ? Math.min(100, data.points / next.maxProgress * 100) : 100 : 0;

  return (
    <div className="container-ingame vip-page">
      <div className="calendar-container">
        <SimpleMenu title={t('vip_title')} />

        <div className="vip-body">
        <section className="vip-summary" aria-label={t('vip_your_points')}>
          <div><span><i className="fa fa-diamond" aria-hidden="true" /> {t('vip_your_points')}</span><strong>{data ? data.points.toLocaleString() : '-'}</strong></div>
          <div><span><i className="fa fa-star" aria-hidden="true" /> {t('vip_current_level')}</span><strong>{data ? t('vip_level_label', {level: current}) : '-'}</strong></div>
          <div className="vip-next"><span>{t('vip_next_level')}</span><strong>{data ? next ? t('vip_level_label', {level: next.level}) : t('vip_all_unlocked') : '-'}</strong><small>{data && next ? t('vip_points_remaining', {points: Math.max(0,next.maxProgress-data.points).toLocaleString()}) : ''}</small></div>
          <div className="vip-summary-progress" role="progressbar" aria-label={t('vip_next_level')} aria-valuenow={Math.round(percent)} aria-valuemin={0} aria-valuemax={100}><span style={{width:`${percent}%`}} /></div>
        </section>
        <div className="vip-table-wrapper">
          <table className="vip-table">
            <thead>
              <tr>
                <th>{t('vip_col_level')}</th>
                <th>{t('vip_col_progress')}</th>
                <th>{t('vip_col_rewards')}</th>
                <th>{t('vip_col_state')}</th>
              </tr>
            </thead>
            <tbody>
              {(isLoading || isError || !data?.levels.length) && <tr><td colSpan={4}>{isLoading ? t('loading_data') : isError ? t('job_network_error') : t('vip_no_levels')}</td></tr>}
              {data?.levels.map((row) => (
                <tr key={row.level} className={row.unlocked ? 'is-unlocked' : row.level === next?.level ? 'is-next' : undefined}>
                  <td>{t('vip_level_label', { level: row.level })}</td>
                  <td>
                    <span className="vip-progress">{data.points.toLocaleString()}</span>/{row.maxProgress.toLocaleString()}
                  </td>
                  <td>
                    <span className="vip-icon"><i className="fa fa-gift" aria-hidden="true" /></span> {row.rewardText}
                    {row.rewardExtra ? (
                      <>
                        <br />
                        <span className="vip-reward-extra">{row.rewardExtra}</span>
                      </>
                    ) : null}
                  </td>
                  <td>
                    <span className={`vip-state ${row.unlocked ? 'vip-state-unlocked' : 'vip-state-locked'}`} style={row.unlocked ? {color: '#e6c98a', fontWeight:600} : undefined}><i className={`fa fa-${row.unlocked ? 'check' : 'lock'}`} aria-hidden="true" /> {t(row.unlocked ? 'vip_state_unlocked' : 'vip_state_locked')}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        </div>
        <footer className="vip-note">
          {t('vip_note')} <strong>{data ? t('vip_points_rate', { points: data.pointsRate }) : '-'}</strong>
          <button type="button" className="vip-info-button" onClick={() => infoRef.current?.showModal()}><i className="fa fa-info-circle" aria-hidden="true" /> {t('vip_information')}</button>
        </footer>
        <dialog ref={infoRef} className="vip-info-dialog" aria-labelledby="vip-info-title" onCancel={e => e.preventDefault()}>
          <header><h2 id="vip-info-title">{t('vip_information')}</h2><button type="button" aria-label={t('vip_close')} onClick={() => infoRef.current?.close()}>×</button></header>
          <div><p>{t('vip_info_account')}</p><p>{t('vip_info_unlock')}</p><p>{t('vip_info_rewards')}</p></div>
          <footer><button type="button" onClick={() => infoRef.current?.close()}>{t('vip_close')}</button></footer>
        </dialog>
      </div>
    </div>
  );
}
